/*
  * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
  * SPDX-License-Identifier: MIT
  *
  * Licensed under the MIT License. See the LICENSE accompanying this file
  * for the specific language governing permissions and limitations under
  * the License.
  */

import React from 'react';
import { Redirect, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { setAuth, setUser } from '../../redux/actions';

import { Auth } from 'aws-amplify';
import { onAuthUIStateChange, AuthState } from '@aws-amplify/ui-components';

import checkForceAuth from './helpers/checkForceAuth';
import handleAuthUIStateChange from './helpers/handleAuthStateChange';
import handleIdpLogin from './helpers/handleIdpLogin';
import { isSettingsFlowRedirect, buildSettingsRedirectSearch } from '../../utils/settingsRedirect';
import { ensureSessionForClient } from '../../utils/swapSessionForClient';

import SignInContainer from './signInContainer';

const mapStateToProps = (state) => {
	return {
		auth: state.app.auth,
		user: state.user,
	}
};

const LandingPage = (props) => {
	const [authState, setAuthState] = React.useState();
	const [loaded, setLoaded] = React.useState(false);
	const [challengeUser, setChallengeUser] = React.useState(null);
	const [cookieRestoreTried, setCookieRestoreTried] = React.useState(false);

	const propSetUser = props.setUser;
	const propSetAuth = props.setAuth;

	handleIdpLogin.bind(this);

	// Run once on mount only — must not re-run after SignedIn updates propSetAuth/propSetUser
	React.useEffect(() => {
		checkForceAuth();
	}, []);

	React.useEffect(() => {
		return onAuthUIStateChange((nextAuthState, authData) => {
			handleAuthUIStateChange(nextAuthState);

			setAuthState(nextAuthState);

			if (nextAuthState === AuthState.ResetPassword) {
				setChallengeUser(authData);
			}

			if (nextAuthState === AuthState.ConfirmSignUp) {
				if (authData && authData.username && authData.signUpAttrs.attributes) {
					let user = {
						id: '',
						username: authData.username,
						attributes: authData.signUpAttrs.attributes
					}
					propSetUser(user);
				}
			}

			if (nextAuthState === AuthState.SignedIn) {
				propSetAuth(true);

				if (authData && authData.username && authData.attributes) {
					let user = {
						id: authData.attributes.sub,
						username: authData.username,
						attributes: authData.attributes
					}
					propSetUser(user);
				}
			}

			setLoaded(true);
		});
	}, [propSetAuth, propSetUser]);

	// settings 誘導時: Amplify 未ログインでも Fast SSO Cookie からセッション復元／Token Swap を試す
	React.useEffect(() => {
		if (!loaded || cookieRestoreTried || authState === AuthState.SignedIn) {
			return;
		}
		const qs = new URLSearchParams(window.location.search);
		const redirect = qs.get('redirect');
		if (!isSettingsFlowRedirect(redirect)) {
			setCookieRestoreTried(true);
			return;
		}

		const targetClientId = qs.get('client_id') || localStorage.getItem('client-id');

		let cancelled = false;
		(async () => {
			try {
				const restored = await ensureSessionForClient(targetClientId);
				if (cancelled || !restored) {
					return;
				}
				const cognitoUser = await Auth.currentAuthenticatedUser();
				const info = await Auth.currentUserInfo();
				propSetAuth(true);
				propSetUser({
					id: (info && info.id) || (cognitoUser && cognitoUser.attributes && cognitoUser.attributes.sub) || '',
					username: (info && info.username) || (cognitoUser && cognitoUser.username) || '',
					attributes: (info && info.attributes) || (cognitoUser && cognitoUser.attributes) || {},
				});
				setAuthState(AuthState.SignedIn);
			} catch (e) {
				console.warn('LandingPage: cookie session restore failed', e);
			} finally {
				if (!cancelled) {
					setCookieRestoreTried(true);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [loaded, cookieRestoreTried, authState, propSetAuth, propSetUser]);

	// redirect=settings/* の場合は OAuth トークン返却ではなく設定画面へ遷移
	if (authState === AuthState.SignedIn && loaded && props.user && props.user.username) {
		const qs = new URLSearchParams(window.location.search);
		const redirect = qs.get('redirect');
		if (isSettingsFlowRedirect(redirect)) {
			const search = buildSettingsRedirectSearch(qs);
			return <Redirect to={{ pathname: `/${redirect}`, search }} />;
		}

		const hasOidcContext = !!qs.get('redirect_uri') || !!localStorage.getItem('client-redirect-uri');
		if (!hasOidcContext) {
			const targetPath = redirect ? `/${redirect}` : '/settings';
			return <Redirect to={targetPath} />;
		}
	}

	return <SignInContainer authState={authState} loaded={loaded} challengeUser={challengeUser} />;
};

export default withRouter(connect(mapStateToProps, { setAuth, setUser })(LandingPage));
